/**
 * Consumption pricing. Customers prepay for credits; every execution is charged in credits from
 * its measured provider cost. Nothing here is customer-visible except packs and credit amounts.
 */
/** 1 credit = $0.01 on the base pack; larger packs include bonus credits. */
export const CREDIT_PACKS = [
    { id: 'starter', name: 'Starter', priceCents: 2_500, credits: 2_500 },
    { id: 'growth', name: 'Growth', priceCents: 10_000, credits: 10_500 },
    { id: 'scale', name: 'Scale', priceCents: 50_000, credits: 55_000 },
    { id: 'enterprise', name: 'Enterprise', priceCents: 200_000, credits: 230_000 },
];
export const creditPack = (id) => CREDIT_PACKS.find((pack) => pack.id === id);
export const packBonusPercent = (pack) => Math.round((pack.credits / pack.priceCents - 1) * 100);
/** Minimum gross margin on usage, before payment-processing fees. */
export const TARGET_MARGIN = 0.5;
/**
 * Credits charged per cent of provider cost. Sized so the pack with the most credits per dollar
 * still earns TARGET_MARGIN; every other pack earns more.
 */
export const CREDITS_PER_COST_CENT = Math.max(...CREDIT_PACKS.map((pack) => pack.credits / pack.priceCents)) / (1 - TARGET_MARGIN);
/** Whole credits for a provider cost, rounded up (the epsilon absorbs float error in the rate). */
export const costCentsToCredits = (costCents) => costCents > 0 ? Math.ceil(costCents * CREDITS_PER_COST_CENT - 1e-9) : 0;
/** Parallel executions per account under consumption billing. */
export const DEFAULT_EXECUTION_SLOTS = 3;
/**
 * Agent tasks that may run at once per account. Tasks run one after another by default so spend
 * stays predictable and each task can build on the last; Manager turns are not limited by this.
 */
export const DEFAULT_TASK_CONCURRENCY = 1;
/**
 * Standard unit costs (cents of provider cost) used for estimates until an account has its own
 * history. Derived from list token prices of $5 / $25 per million input / output tokens:
 * a Manager turn ≈ 30k in + 3k out, each planned step adds ≈ 8k in + 1k out of tool round trips,
 * and a worker task ≈ 25k in + 4k out, plus 25% for the Manager reviewing worker results.
 */
export const STANDARD_UNIT_COSTS = {
    managerTurnCents: 23,
    planStepCents: 7,
    workerTaskCents: 29,
};
const roundUpTo = (value, step) => (value <= 0 ? 0 : Math.ceil(value / step) * step);
export const estimatePlanCredits = (stepCount, workload, unitCosts = STANDARD_UNIT_COSTS, basis = 'standard') => ({
    setupCredits: roundUpTo(costCentsToCredits(unitCosts.managerTurnCents + Math.max(0, stepCount) * unitCosts.planStepCents
        + Math.max(0, workload.initialTasks) * unitCosts.workerTaskCents) + Math.max(0, workload.setupMediaCredits ?? 0), 10),
    monthlyCredits: roundUpTo(costCentsToCredits(Math.max(0, workload.monthlyTasks) * unitCosts.workerTaskCents)
        + Math.max(0, workload.monthlyMediaCredits ?? 0), 10),
    basis,
});
/**
 * SMS provider list prices (US), in cents of cost. Numbers bill monthly; each message in either
 * direction bills once. Customer prices follow from costCentsToCredits, keeping TARGET_MARGIN.
 */
export const SMS_COSTS = { numberMonthlyCents: 115, messageCents: 0.83 };
export const smsPricing = () => ({
    numberMonthlyCredits: costCentsToCredits(SMS_COSTS.numberMonthlyCents),
    messageCredits: costCentsToCredits(SMS_COSTS.messageCents),
});
/**
 * Media generation list prices, in cents of cost. Images bill per image by quality and size; videos
 * bill per second of output by quality and resolution. A small allowance covers prompt input.
 */
export const MEDIA_COSTS = {
    image: {
        standard: { '1K': 6.7, '2K': 10.1, '4K': 15.1 },
        high: { '1K': 13.4, '2K': 13.4, '4K': 24 },
    },
    videoPerSecond: {
        standard: { '720p': 10, '1080p': 12 },
        high: { '720p': 40, '1080p': 40 },
    },
    inputAllowanceCents: 0.5,
};
export const imageCredits = (quality, size, count = 1) => costCentsToCredits((MEDIA_COSTS.image[quality][size] + MEDIA_COSTS.inputAllowanceCents) * Math.max(1, count));
export const videoCredits = (quality, resolution, seconds) => costCentsToCredits(MEDIA_COSTS.videoPerSecond[quality][resolution] * Math.max(1, seconds) + MEDIA_COSTS.inputAllowanceCents);
/** Remote browser list price, in cents of cost per minute (billed by the minute, one-minute minimum). */
export const BROWSER_COSTS = { minuteCents: 0.2 };
export const browserCredits = (minutes) => costCentsToCredits(Math.max(1, Math.ceil(minutes)) * BROWSER_COSTS.minuteCents);
/** Domain that receives Manager and agent email. */
export const INBOX_DOMAIN = 'inbox.skiphr.ai';
